import { useCallback, useState } from "react";
import type { CreateServiceRequest, HttpAuth, HttpAuthType } from "@/types/api";
import { HttpFields } from "./HttpFields";
import { parseOptionalInt } from "./parseOptionalInt";

interface HttpAuthState {
  authType: HttpAuthType | "none";
  bearerToken: string;
  basicUsername: string;
  basicPassword: string;
  apiKeyKey: string;
  apiKeyParamName: string;
  headerName: string;
  headerValue: string;
}

function buildHttpAuth(state: HttpAuthState): HttpAuth | undefined {
  switch (state.authType) {
    case "bearer":
      return { type: "bearer", token: state.bearerToken };
    case "basic":
      return {
        type: "basic",
        username: state.basicUsername,
        password: state.basicPassword,
      };
    case "api_key":
      return {
        type: "api_key",
        key: state.apiKeyKey,
        param_name: state.apiKeyParamName || "api_key",
      };
    case "header":
      return {
        type: "header",
        name: state.headerName,
        value: state.headerValue,
      };
    default:
      return undefined;
  }
}

export function useHttpFields() {
  const [httpHost, setHttpHost] = useState("");
  const [maxRetries, setMaxRetries] = useState("5");
  const [connectionTimeoutSec, setConnectionTimeoutSec] = useState("30");
  const [timeoutSec, setTimeoutSec] = useState("30");

  const [authType, setAuthType] = useState<HttpAuthType | "none">("none");
  const [bearerToken, setBearerToken] = useState("");
  const [basicUsername, setBasicUsername] = useState("");
  const [basicPassword, setBasicPassword] = useState("");
  const [apiKeyKey, setApiKeyKey] = useState("");
  const [apiKeyParamName, setApiKeyParamName] = useState("api_key");
  const [headerName, setHeaderName] = useState("");
  const [headerValue, setHeaderValue] = useState("");

  const toPayload = useCallback((): Partial<CreateServiceRequest> => {
    const payload: Partial<CreateServiceRequest> = {
      host: httpHost,
    };
    payload.max_retries = parseOptionalInt(maxRetries);
    payload.connection_timeout_sec = parseOptionalInt(connectionTimeoutSec);
    payload.timeout_sec = parseOptionalInt(timeoutSec);
    const auth = buildHttpAuth({
      authType,
      bearerToken,
      basicUsername,
      basicPassword,
      apiKeyKey,
      apiKeyParamName,
      headerName,
      headerValue,
    });
    if (auth) payload.auth = auth;
    return payload;
  }, [
    httpHost,
    maxRetries,
    connectionTimeoutSec,
    timeoutSec,
    authType,
    bearerToken,
    basicUsername,
    basicPassword,
    apiKeyKey,
    apiKeyParamName,
    headerName,
    headerValue,
  ]);

  const fields = (
    <HttpFields
      httpHost={httpHost}
      setHttpHost={setHttpHost}
      maxRetries={maxRetries}
      setMaxRetries={setMaxRetries}
      connectionTimeoutSec={connectionTimeoutSec}
      setConnectionTimeoutSec={setConnectionTimeoutSec}
      timeoutSec={timeoutSec}
      setTimeoutSec={setTimeoutSec}
      authType={authType}
      setAuthType={setAuthType}
      bearerToken={bearerToken}
      setBearerToken={setBearerToken}
      basicUsername={basicUsername}
      setBasicUsername={setBasicUsername}
      basicPassword={basicPassword}
      setBasicPassword={setBasicPassword}
      apiKeyKey={apiKeyKey}
      setApiKeyKey={setApiKeyKey}
      apiKeyParamName={apiKeyParamName}
      setApiKeyParamName={setApiKeyParamName}
      headerName={headerName}
      setHeaderName={setHeaderName}
      headerValue={headerValue}
      setHeaderValue={setHeaderValue}
    />
  );

  return { fields, toPayload };
}
